// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'

/**
 * The `<qds-table-cell>` element defines a cell of a table that contains data.
 * It participates in the *table model*.
 *
 * @see https://quartz.se.com/build/components/table
 */
@Component({
  tag: 'qds-table-cell',
  shadow: true,
  styleUrl: 'table-cell.css',
})
export class TableCell implements ComponentInterface {
  /**
   * Non-negative integer value that indicates for how many columns the cell
   * extends. Its default value is `1`. Values higher than 1000 will be
   * considered as incorrect and will be set to the default value (1).
   *
   * @webnative
   */
  @Prop({ attribute: 'colspan' }) public readonly colSpan?: number

  /**
   * Non-negative integer value that indicates for how many rows the cell
   * extends. Its default value is `1`; if its value is set to `0`, it extends
   * until the end of the table section (`<qds-table-head>` or
   * `<qds-table-body>`, even if implicitly defined), that the cell belongs to.
   * Values higher than 65534 are clipped down to 65534.
   *
   * @webnative
   */
  @Prop({ attribute: 'rowspan' }) public readonly rowSpan?: number

  @Element() private readonly host!: HTMLElement

  #inheritedAttributes: Attributes = {}

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <td
        class="qds-table-cell"
        colSpan={this.colSpan}
        rowSpan={this.rowSpan}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        <slot />
      </td>
    )
  }
}
